<template>
  <div class="search-results">
    <div
      ref="searchResultsContainerRef"
      class="scroll-wrapper"
    >
      <div class="filters-section">
        <DateDropdown @filter-applied="$emit('filterApplied', $event)" />
        <SchoolsDropdown @filter-applied="$emit('filterApplied', $event)" />
        <TypeDropdown @filter-applied="$emit('filterApplied', $event)" />
        <button
          v-if="hasActiveFilters"
          class="clear-all-filters-btn"
          :aria-label="$t('search.clearAllFiltersAriaLabel')"
          @click="handleClearAllFilters"
        >
          {{ $t('search.clearAllFilters') }}
        </button>
      </div>
      <div
        class="results-section"
        aria-live="polite"
        :aria-label="
          isLoading
            ? $t('search.loadingResultsAriaLabel')
            : !hasResults
              ? $t('search.noResultsAriaLabel', { searchTerm: route.query.q || '' })
              : $t('search.resultsCountAriaLabel', { count: results.length, searchTerm: route.query.q || '' })
        "
      >
        <AiAnswerCard
          v-if="showAiAnswer"
          :key="aiData.answer"
          :ai-data="aiData"
          @continue-in-chat="$emit('continueInChat')"
        />
        <template v-if="isLoading">
          <BaseCardSkeleton
            v-for="n in 3"
            :key="`skeleton-${n}`"
          />
        </template>
        <div
          v-else-if="!hasResults"
          class="no-results"
        >
          <span class="error-message">{{ $t('search.noResultsFor', { searchTerm: route.query.q }) }}</span>
          <span class="try-again-message">{{ $t('search.clearFiltersAndSearchAgain') }}</span>
        </div>
        <ResultsSection
          v-else
          :results="results"
          @result-clicked="$emit('result-clicked', $event)"
          @open-feedback-modal="$emit('openFeedbackModal')"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { watch } from 'vue';
import { useRoute } from 'vue-router';
import { useSearchFilters } from '../../composables/useSearchFilters';
import { useSearchResultsContainer } from '../../composables/useSearchResultsContainer';
import AiAnswerCard from './ResultCards/AiAnswerCard.vue';
import BaseCardSkeleton from './ResultCards/BaseCardSkeleton.vue';
import ResultsSection from './ResultsSection.vue';
import DateDropdown from './SearchFilters/DateDropdown.vue';
import SchoolsDropdown from './SearchFilters/SchoolsDropdown.vue';
import TypeDropdown from './SearchFilters/TypeDropdown.vue';

const props = defineProps({
  results: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  aiData: {
    type: Object,
    default: () => ({
      answer: '',
      references: [],
      status: '',
      isLoading: false,
    }),
  },
  showAiAnswer: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['result-clicked', 'filterApplied', 'openFeedbackModal', 'continueInChat']);

const route = useRoute();
const { hasActiveFilters, clearAllFilters } = useSearchFilters();
const { setContainer, setResults, hasResults } = useSearchResultsContainer();

const searchResultsContainerRef = useTemplateRef('searchResultsContainerRef');

// Set the container ref when the element is available
watch(
  searchResultsContainerRef,
  (newValue) => {
    if (newValue) {
      setContainer(newValue);
    }
  },
  { immediate: true },
);

// Set the results in the composable when they change
watch(
  () => props.results,
  (newResults) => {
    setResults(newResults);
  },
  { immediate: true },
);

function handleClearAllFilters() {
  clearAllFilters();
  // Clear all filter query parameters
  const newQuery = { ...route.query };
  delete newQuery.secondary_organizations;
  delete newQuery.query_indices;
  delete newQuery.start_date;
  delete newQuery.end_date;
  navigateTo({ query: newQuery }, { replace: true });
}
</script>

<style lang="scss" scoped>
@use '../global/scss/mixins/_breakpoints.scss' as bp;

.search-results {
  --space-150: 6px;
  --space-200: 8px;
  --space-300: 12px;
  --space-400: 16px;
  --color-text-primary: #2d2d2d;
  --color-grey-75: #e9e9e9;
  --color-grey-100: #e0e0e0;
  --color-grey-200: #d3d3d3;
  --color-grey-300: #b3b3b3;
  --color-blue-500: #1850c1;
  --z-index-search-filters: 1;

  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  width: 100%;
  background: var(--primary-white);
  border-radius: 8px;
  height: auto;
  overflow: hidden;
}

.scroll-wrapper {
  padding: 0 16px 16px 16px;
  overflow-y: auto;
}

.no-results {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 16px;
  font-size: 14px;
  font-weight: 450;
  line-height: 16px;
  letter-spacing: 0.5px;
  color: #222;
  height: 240px;

  .error-message {
    color: var(--color-text-primary);
    font-size: 16px;
    font-weight: 700;
    line-height: 120%;
    letter-spacing: 0.8px;
  }
  .try-again-message {
    color: var(--color-text-primary);
    font-size: 16px;
    font-style: normal;
    font-weight: 450;
    line-height: 22px;
    letter-spacing: 0.6px;
  }
}

.filters-section {
  position: sticky;
  top: 0;
  background: var(--primary-white);
  z-index: var(--z-index-search-filters);
  margin: 0 -16px;
  padding: 16px;
  display: flex;
  gap: 12px;
}

.clear-all-filters-btn {
  border-radius: var(--tertiary-button-border-radius);
  background: transparent;
  border: none;
  padding: var(--space-150) var(--space-300);
  font-size: 12px;
  font-weight: 450;
  line-height: 16px;
  letter-spacing: 0.1px;
  color: var(--tertiary-button-text-contrast);
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus-visible {
    outline: var(--tertiary-button-focus-outline);
  }

  &:hover {
    background-color: var(--tertiary-button-hover);
    outline: var(--tertiary-button-hover-border);
  }
}

.results-section {
  gap: 16px;
  display: flex;
  flex-direction: column;
  padding-top: 8px;
}

@include bp.screen('<=md') {
  .search-results {
    height: calc(100vh - var(--search-bar-height, 74px));
  }
}

@include bp.screen('xs') {
  .search-results {
    border-radius: 0;
  }
}
</style>
